import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@/lib/router-compat';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { destinationsFor, searchTextFor, type NavAudience, type NavDestination } from '@/lib/navigation';
import { ArrowRight, Search } from 'lucide-react';

interface CommandPaletteProps {
  audience: NavAudience | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Split a query into lowercase tokens worth highlighting. */
function queryTokens(query: string): string[] {
  return Array.from(
    new Set(
      query
        .toLowerCase()
        .split(/[^a-z0-9]+/i)
        .filter((t) => t.length > 1),
    ),
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Renders text with every query token wrapped in a <mark>. */
export function HighlightedText({ text, tokens }: { text: string; tokens: string[] }) {
  if (tokens.length === 0) return <>{text}</>;
  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'ig');
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) =>
        tokens.includes(part.toLowerCase()) ? (
          <mark key={i} className="bg-accent/25 text-inherit rounded-[2px] px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/** Keywords that matched the query but are not already visible in the row. */
function matchedKeywords(destination: NavDestination, tokens: string[]): string[] {
  if (tokens.length === 0) return [];
  const visible = `${destination.label} ${destination.description}`.toLowerCase();
  return (destination.keywords ?? [])
    .filter((k) => tokens.some((t) => k.toLowerCase().includes(t)) && !visible.includes(k.toLowerCase()))
    .slice(0, 3);
}

/** Right-hand preview of what the highlighted destination will show. */
function DestinationPreview({ destination, tokens }: { destination: NavDestination | null; tokens: string[] }) {
  if (!destination) {
    return (
      <div className="hidden md:flex flex-col items-center justify-center h-full text-center text-xs text-muted-foreground px-6">
        <Search className="h-5 w-5 mb-2 opacity-50" aria-hidden="true" />
        Highlight a result to preview what that page shows.
      </div>
    );
  }

  return (
    <div className="hidden md:block h-full overflow-y-auto p-4" aria-live="polite">
      <div className="flex items-center gap-2 mb-1">
        <destination.icon className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
        <p className="font-medium text-sm">{destination.label}</p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{destination.description}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
        What you'll see
      </p>
      <ul className="space-y-1.5 mb-3">
        {(destination.preview ?? ['Opens this page.']).map((line) => (
          <li key={line} className="flex gap-2 text-xs text-foreground">
            <span aria-hidden="true" className="text-accent">•</span>
            <span>
              <HighlightedText text={line} tokens={tokens} />
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] font-mono text-muted-foreground break-all">{destination.to}</p>
    </div>
  );
}

/** Global "jump to anything" search, opened with Cmd/Ctrl+K. */
export function CommandPalette({ audience, open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const destinations = destinationsFor(audience);
  const [query, setQuery] = useState('');
  const [activeValue, setActiveValue] = useState('');

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveValue('');
    }
  }, [open]);

  const tokens = useMemo(() => queryTokens(query), [query]);
  const groups = Array.from(new Set(destinations.map((d) => d.group)));
  const activeDestination = destinations.find((d) => d.to === activeValue) ?? destinations[0] ?? null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-3xl"
      commandProps={{
        value: activeValue,
        onValueChange: setActiveValue,
        // Match against labels, descriptions, synonyms and preview copy.
        filter: (value, search) => {
          const destination = destinations.find((d) => d.to === value);
          if (!destination) return 0;
          const haystack = searchTextFor(destination).toLowerCase();
          const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
          if (terms.length === 0) return 1;
          if (!terms.every((t) => haystack.includes(t))) return 0;
          // Rank label matches above synonym-only matches.
          return destination.label.toLowerCase().includes(terms[0]) ? 1 : 0.5;
        },
      }}
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search pages — try “invites”, “sprint”, “audit”…"
      />
      <div className="flex md:divide-x">
        <div className="flex-1 min-w-0">
          <CommandList className="max-h-[340px]">
            <CommandEmpty>
              No matching page. Try a simpler word like “startup”, “invite” or “events”.
            </CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group} heading={group}>
                {destinations
                  .filter((d) => d.group === group)
                  .map((d) => {
                    const synonyms = matchedKeywords(d, tokens);
                    return (
                      <CommandItem
                        key={d.to}
                        value={d.to}
                        onSelect={() => {
                          onOpenChange(false);
                          navigate(d.to);
                        }}
                        className="flex items-start gap-3 py-2.5"
                      >
                        <d.icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="flex flex-col min-w-0">
                          <span className="font-medium">
                            <HighlightedText text={d.label} tokens={tokens} />
                          </span>
                          <span className="text-xs text-muted-foreground">
                            <HighlightedText text={d.description} tokens={tokens} />
                          </span>
                          {synonyms.length > 0 && (
                            <span className="text-[11px] text-muted-foreground mt-0.5">
                              Also matches:{' '}
                              <HighlightedText text={synonyms.join(', ')} tokens={tokens} />
                            </span>
                          )}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 ml-auto mt-1 shrink-0 opacity-0 data-[selected=true]:opacity-60" aria-hidden="true" />
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
            ))}
          </CommandList>
        </div>
        <div className="hidden md:block w-72 shrink-0 max-h-[340px]">
          <DestinationPreview destination={activeDestination} tokens={tokens} />
        </div>
      </div>
    </CommandDialog>
  );
}

/** Convenience hook for pages that want their own trigger button. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
